import React from "react";
import {
  Close,
  Settings as SettingsIcon,
  Videocam as VideocamIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Dialog } from "@mui/material";
import { DialogTitle } from "@mui/material";
import { DialogContent } from "@mui/material";
import { DialogActions } from "@mui/material";
import { useState, useEffect } from "react";
import { Typography } from "@mui/material";
import SettingsGroup from "../../components/myDrawingSection/SettingsGroup.js";
import {
  SettingsSelect,
  SettingsSwitch,
  SettingsSlider,
} from "../../components/myDrawingSection/SettingsControls.js";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchUserDisplaySettings,
  saveUserDisplaySettings,
  resetUserDisplaySettings,
  setDisplayTabSettings,
} from "../../store/slices/userSlice.js";

export function TabSettingsComponent({
  tooltipText,
  title,
  children,
  onReset,
  onCancel,
  onSave,
  onOpenChange,
  tabKey,
}) {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const displayTabSettings = useSelector(
    (state) => state.user?.displayTabSettings || {}
  );
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    if (tabKey) {
      dispatch(fetchUserDisplaySettings(tabKey));
      setSettings(displayTabSettings);
    }
  }, [dispatch, tabKey, isOpen]);

  useEffect(() => {
    if (displayTabSettings) {
      setSettings(displayTabSettings);
    }
  }, [displayTabSettings]);

  const handleToggle = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    if (onOpenChange) {
      onOpenChange(newIsOpen);
    }
  };

  const handleCancel = () => {
    // Reload settings from backend to discard any unsaved changes
    if (tabKey) {
      dispatch(fetchUserDisplaySettings(tabKey));
    }
    setIsOpen(false);
    if (onCancel) {
      onCancel();
    }
  };

  const handleReset = async () => {
    if (tabKey) {
      try {
        dispatch(resetUserDisplaySettings({ tabKey }));
      } catch (error) {
        console.error("Error resetting display settings:", error);
      }
    }
  };

  const handleSave = async () => {
    if (!tabKey || !settings[tabKey]) {
      console.error("Tab key and settings are required for saving");
      return;
    }

    try {
      const result = await dispatch(
        saveUserDisplaySettings({ tabKey, settings: settings[tabKey] })
      ).unwrap();

      setIsOpen(false);
      // if (onSave) {
      //   onSave();
      // }
    } catch (error) {
      console.error("Error saving display settings:", error);
    }
  };
  const handleChangeDefaultView = (val) => {
    setSettings({
      ...settings,
      [tabKey]: { ...settings[tabKey], defaultView: val },
    });
  };
  const handleChangeShowThumbnails = (val) => {
    setSettings({
      ...settings,
      [tabKey]: { ...settings[tabKey], showThumbnails: val },
    });
  };
  const handleChangeShowFileSize = (val) => {
    setSettings({
      ...settings,
      [tabKey]: { ...settings[tabKey], showFileSize: val },
    });
  };
  const handleChangeShowModifiedDate = (val) => {
    setSettings({
      ...settings,
      [tabKey]: { ...settings[tabKey], showModifiedDate: val },
    });
  };
  const handleChangeShowDuration = (val) => {
    setSettings({
      ...settings,
      [tabKey]: { ...settings[tabKey], showDuration: val },
    });
  };
  const handleChangeShowLocation = (val) => {
    setSettings({
      ...settings,
      [tabKey]: { ...settings[tabKey], showLocation: val },
    });
  };
  const handleChangeDefaultSortBy = (val) => {
    setSettings({
      ...settings,
      [tabKey]: { ...settings[tabKey], defaultSortBy: val },
    });
  };
  const handleChangeEnableFavorites = (val) => {
    setSettings({
      ...settings,
      [tabKey]: { ...settings[tabKey], enableFavorites: val },
    });
  };
  const handleChangeDefaultVolume = (val) => {
    setSettings({
      ...settings,
      [tabKey]: { ...settings[tabKey], defaultVolume: val },
    });
  };
  const handleChangeEnableAutoPlay = (val) => {
    setSettings({
      ...settings,
      [tabKey]: { ...settings[tabKey], enableAutoPlay: val },
    });
  };
  const handleChangeShowControls = (val) => {
    setSettings({
      ...settings,
      [tabKey]: { ...settings[tabKey], showControls: val },
    });
  };
  const handleChangeLoopVideo = (val) => {
    setSettings({
      ...settings,
      [tabKey]: { ...settings[tabKey], loopVideo: val },
    });
  };
  const myDrawingsSettings = () => {
    // Use Redux state directly
    const tabSettings = settings?.[tabKey] ?? {};

    return (
      <Box>
        <SettingsGroup
          title="Display Settings"
          description="Configure how files and folders are displayed"
        >
          <SettingsSelect
            label="Default View"
            value={tabSettings?.defaultView || "list"}
            onChange={handleChangeDefaultView}
            options={[
              { value: "list", label: "List View" },
              { value: "grid", label: "Grid View" },
            ]}
            description="Choose the default layout for file display"
          />

          <SettingsSwitch
            label="Show Thumbnails"
            checked={tabSettings?.showThumbnails ?? true}
            onChange={(checked) => {
              // Update Redux state immediately
              handleChangeShowThumbnails(checked);
            }}
            description="Display file preview thumbnails"
          />

          <SettingsSwitch
            label="Show File Size"
            checked={tabSettings?.showFileSize ?? true}
            onChange={(checked) => {
              // Update Redux state immediately
              handleChangeShowFileSize(checked);
            }}
            description="Display file size information"
          />

          <SettingsSwitch
            label="Show Modified Date"
            checked={tabSettings?.showModifiedDate ?? true}
            onChange={(checked) => {
              // Update Redux state immediately
              handleChangeShowModifiedDate(checked);
            }}
            description="Display last modified date"
          />
        </SettingsGroup>
        <Divider sx={{ my: 2, borderColor: "#E5E7EB" }} />

        <SettingsGroup
          title="Sorting & Organization"
          description="Configure file sorting and organization options"
        >
          <SettingsSelect
            label="Default Sort By"
            value={tabSettings?.defaultSortBy || "name"}
            onChange={handleChangeDefaultSortBy}
            options={[
              { value: "name", label: "Name" },
              { value: "dateModified", label: "Date Modified" },
              { value: "size", label: "Size" },
            ]}
            description="Choose how files are sorted by default"
          />
        </SettingsGroup>
        <Divider sx={{ my: 2, borderColor: "#E5E7EB" }} />

        <SettingsGroup
          title="Features"
          description="Enable or disable specific features"
        >
          <SettingsSwitch
            label="Enable Favorites"
            checked={tabSettings?.enableFavorites ?? true}
            onChange={(checked) => {
              // Update Redux state immediately
              handleChangeEnableFavorites(checked);
            }}
            description="Allow marking files as favorites"
          />
        </SettingsGroup>
      </Box>
    );
  };

  const siteRecordingsSettings = () => {
    // Use Redux state directly
    const tabSettings = settings?.[tabKey] ?? {};

    return (
      <Box>
        <SettingsGroup
          title="Display Settings"
          description="Configure how recordings are displayed"
        >
          <SettingsSwitch
            label="Show Thumbnails"
            checked={tabSettings?.showThumbnails ?? true}
            onChange={(checked) => {
              // Update Redux state immediately
              handleChangeShowThumbnails(checked);
            }}
            description="Display video preview thumbnails"
          />
          <SettingsSwitch
            label="Show Duration"
            checked={tabSettings?.showDuration ?? true}
            onChange={(checked) => {
              // Update Redux state immediately
              handleChangeShowDuration(checked);
            }}
            description="Display recording duration"
          />
          <SettingsSwitch
            label="Show Location"
            checked={tabSettings?.showLocation ?? true}
            onChange={(checked) => {
              // Update Redux state immediately
              handleChangeShowLocation(checked);
            }}
            description="Display recording location information"
          />
        </SettingsGroup>

        <SettingsGroup
          title="Sorting"
          description="Configure recording sort order"
        >
          <SettingsSelect
            label="Sort By"
            value={tabSettings?.defaultSortBy || "timestamp"}
            onChange={handleChangeDefaultSortBy}
            options={[{ value: "timestamp", label: "Date & Time" }]}
            description="Choose how recordings are sorted"
          />
        </SettingsGroup>
      </Box>
    );
  };

  const recVideoSettings = () => {
    // Use Redux state directly
    const tabSettings = settings?.[tabKey] ?? {};

    return (
      <Box>
        {/* Description Text */}
        <Typography
          variant="body2"
          sx={{
            fontSize: 12,
            color: "#4A5565",
            mb: 3,
            fontFamily: "Roboto, sans-serif",
          }}
        >
          Configure your 360 Stream Viewer preferences. Settings are loaded from
          /config/pageSettings.config.js
        </Typography>

        {/* Video Player Settings Section */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 2,
            }}
          >
            <VideocamIcon
              sx={{
                fontSize: 20,
                color: "#212121",
              }}
            />
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                fontWeight: 600,
                color: "#FF8555",
              }}
            >
              Video Player Settings
            </Typography>
          </Box>

          {/* Default Volume */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 14,
                  color: "#212121",
                }}
              >
                Default Volume: {tabSettings?.defaultVolume ?? 70}%
              </Typography>
            </Box>
            <Box sx={{ px: 0.5 }}>
              <SettingsSlider
                label=""
                value={tabSettings?.defaultVolume ?? 70}
                onChange={handleChangeDefaultVolume}
                min={0}
                max={100}
                step={1}
                valueDisplay={() => ""}
              />
            </Box>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: 11,
                color: "#4A5565",
                mt: 0.5,
                fontStyle: "italic",
              }}
            >
              From: RECORDINGS_SETTINGS.VIDEO.DEFAULT_VOLUME
            </Typography>
          </Box>

          {/* Auto-play videos */}
          <Box sx={{ mb: 3 }}>
            <SettingsSwitch
              label="Auto-play videos"
              checked={tabSettings?.enableAutoPlay ?? false}
              onChange={(checked) => {
                handleChangeEnableAutoPlay(checked);
              }}
              description=""
            />
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: 11,
                color: "#4A5565",
                mt: 0.5,
                ml: 6.5,
                fontStyle: "italic",
              }}
            >
              RECORDINGS_SETTINGS.VIDEO.ENABLE_AUTO_PLAY
            </Typography>
          </Box>

          {/* Show video controls */}
          <Box sx={{ mb: 3 }}>
            <SettingsSwitch
              label="Show video controls"
              checked={tabSettings?.showControls ?? true}
              onChange={(checked) => {
                handleChangeShowControls(checked);
              }}
              description=""
            />
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: 11,
                color: "#4A5565",
                mt: 0.5,
                ml: 6.5,
                fontStyle: "italic",
              }}
            >
              RECORDINGS_SETTINGS.VIDEO.SHOW_CONTROLS
            </Typography>
          </Box>

          {/* Loop video playback */}
          <Box sx={{ mb: 3 }}>
            <SettingsSwitch
              label="Loop video playback"
              checked={tabSettings?.loopVideo ?? false}
              onChange={(checked) => {
                handleChangeLoopVideo(checked);
              }}
              description=""
            />
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: 11,
                color: "#4A5565",
                mt: 0.5,
                ml: 6.5,
                fontStyle: "italic",
              }}
            >
              RECORDINGS_SETTINGS.VIDEO.LOOP_VIDEO
            </Typography>
          </Box>
        </Box>

        {/* Informational Box */}
        <Box
          sx={{
            bgcolor: "#E8F5E9",
            border: "1px solid #C8E6C9",
            borderRadius: 1,
            p: 2,
            mt: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1.5,
            }}
          >
            <CheckCircleIcon
              sx={{
                fontSize: 20,
                color: "#4CAF50",
              }}
            />
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                fontWeight: 600,
                color: "#2E7D32",
              }}
            >
              Settings Loaded from Constants
            </Typography>
          </Box>
          <Box
            component="ul"
            sx={{
              m: 0,
              pl: 3,
              "& li": {
                fontFamily: "Roboto, sans-serif",
                fontSize: 12,
                color: "#2E7D32",
                mb: 0.5,
              },
            }}
          >
            <li>These settings use values from RECORDINGS_SETTINGS</li>
            <li>Colors use APP_SETTINGS.COLORS</li>
            <li>Typography uses APP_SETTINGS.TYPOGRAPHY</li>
          </Box>
        </Box>
      </Box>
    );
  };

  const handleRenderChildren = () => {
    if (children) {
      return children;
    }

    if (tabKey === "my-drawings") {
      return myDrawingsSettings();
    } else if (tabKey === "site-recordings") {
      return siteRecordingsSettings();
    } else if (tabKey === "rec-video") {
      return recVideoSettings();
    }

    return null;
  };
  return (
    <>
      {/* <IconButton
        sx={{
          color: "#4A5565",
          "&:hover": {
            backgroundColor: "#E5E7EB",
            color: "#2932E5",
          },
        }}
        onClick={handleToggle}
      >
        <SettingsIcon />
      </IconButton> */}
      <Tooltip title={tooltipText} arrow placement="left">
        <IconButton
          onClick={handleToggle}
          size="small"
          aria-label="Settings"
          className="settings-icon"
          sx={{
            // R9: Material Design 3 Interaction States
            color: isOpen ? "#2932E5" : "#4A5565",
            bgcolor: isOpen ? "rgba(41, 50, 229, 0.08)" : "transparent",
            opacity: isOpen ? 1 : 0.7,

            // R2: 40px touch target (WCAG 2.2 Level AA)
            width: 40,
            height: 40,

            // R6: Smooth transitions (200ms ease-out)
            transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",

            // R9: Hover state
            "&:hover": {
              color: "#2932E5",
              bgcolor: "rgba(41, 50, 229, 0.08)",
              opacity: 1,
            },

            // R10: Focus state (2px focus ring)
            "&:focus-visible": {
              outline: "2px solid #2932E5",
              outlineOffset: 2,
            },

            // R9: Active state (scale down)
            "&:active": {
              transform: "scale(0.95)",
            },

            // R9: Disabled state
            "&:disabled": {
              opacity: 0.38,
              cursor: "not-allowed",
            },

            // R6: Pulse animation for first-time users (if needed)
            ...(isOpen && {
              "@keyframes pulse": {
                "0%, 100%": { opacity: 0.7 },
                "50%": { opacity: 1, transform: "scale(1.05)" },
              },
              animation: "pulse 2s ease-in-out 3",
            }),
          }}
        >
          {/* R9: Badge indicator for modified settings */}
          <Badge
            variant="dot"
            color="primary"
            invisible={!isOpen}
            sx={{
              "& .MuiBadge-dot": {
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: "#2932E5",
                top: 6,
                right: 6,
              },
            }}
          >
            <SettingsIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      {isOpen && (
        <>
          <Dialog
            open={isOpen}
            onClose={handleToggle}
            maxWidth="sm"
            fullWidth
            sx={{
              "& .MuiDialog-paper": {
                color: "#212121",
                boxShadow: "var(--Paper-shadow)",
                backgroundImage: "var(--Paper-overlay)",
                width: "90vw",
                maxWidth: "560px",
                height: "auto",
                maxHeight: "90vh",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#FFFFFF",
                transition: "box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1)",
                borderRadius: "20px",
                overflow: "hidden",
              },
            }}
          >
            <DialogTitle
              sx={{
                justifyContent: "space-between",
                display: "flex",
                p: 2,
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  margin: 0,
                  lineHeight: "1.5",
                  color: "#ff8555",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "20px",
                  fontWeight: 600,
                }}
              >
                {title}
              </Typography>
              <IconButton
                onClick={handleToggle}
                size="small"
                sx={{
                  color: "#4A5565",
                  "&:hover": {
                    bgcolor: "rgba(41, 50, 229, 0.08)",
                  },
                }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent>{handleRenderChildren()}</DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  width: "100%",
                }}
              >
                {/* R11: Reset to Defaults button */}
                {true && (
                  <Button
                    onClick={handleReset}
                    variant="text"
                    sx={{
                      color: "#D32F2F",
                      textTransform: "none",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: 14,
                      fontWeight: 600,
                      "&:hover": {
                        bgcolor: "rgba(211, 47, 47, 0.08)",
                      },
                    }}
                  >
                    Reset to Defaults
                  </Button>
                )}

                {/* Cancel and Apply buttons */}
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    onClick={handleCancel}
                    variant="outlined"
                    sx={{
                      textTransform: "none",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: 14,
                      fontWeight: 500,
                      color: "#4A5565",
                      borderColor: "#E5E7EB",
                      "&:hover": {
                        borderColor: "#2932E5",
                        bgcolor: "rgba(41, 50, 229, 0.04)",
                      },
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    onClick={handleSave}
                    variant="contained"
                    sx={{
                      textTransform: "none",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: 14,
                      fontWeight: 500,
                      bgcolor: "#2932E5",
                      color: "#FFFFFF",
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "#1e28b8",
                        boxShadow: "0 2px 4px rgba(41, 50, 229, 0.3)",
                      },
                    }}
                  >
                    Apply
                  </Button>
                </Box>
              </Box>
            </DialogActions>
          </Dialog>
        </>
      )}
    </>
  );
}
